/**
 * MarketplaceListingCard - Card component for displaying script listings in the marketplace
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseInit';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { formatPrice, formatRating, getCategoryById } from './MarketplaceCategories';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const MarketplaceListingCard = ({ listing, showActions = true }) => {
  const navigate = useNavigate();
  const { categories, hasPurchased, isOwner, loadMyPurchases } = useMarketplace();
  const { userTeam, effectiveTeamId } = useAuth();
  const { refreshTemplates } = useData();
  
  const targetTeamId = effectiveTeamId || userTeam;
  
  const category = getCategoryById(listing.category, categories);
  const isPurchased = hasPurchased(listing.id);
  const isOwnListing = isOwner(listing);

  const [acquiring, setAcquiring] = useState(false);
  const [acquired, setAcquired] = useState(false);
  const [acquireError, setAcquireError] = useState(null);

  const handleQuickAcquire = async (e) => {
    e.stopPropagation();
    if (acquiring || acquired) return;
    
    setAcquiring(true);
    setAcquireError(null);
    try {
      const acquireScript = httpsCallable(functions, 'purchaseFreeScript');
      const result = await acquireScript({
        listingId: listing.id,
        buyerTeamId: targetTeamId,
      });
      if (!result.data?.success) {
        throw new Error('Failed to acquire script.');
      }
      await Promise.all([loadMyPurchases(), refreshTemplates()]);
      setAcquired(true);
    } catch (err) {
      const message = err.code === 'already-exists'
        ? 'Already acquired'
        : (err.message || 'Failed. Try again.');
      setAcquireError(message);
    } finally {
      setAcquiring(false);
    }
  };
  
  const handleViewDetails = () => {
    navigate(`/marketplace/listing/${listing.id}`);
  };
  
  return (
    <Card 
      hover 
      padding="none"
      className="overflow-hidden transition-all duration-200 hover:shadow-lg"
    >
      {/* Header with category badge */}
      <div className="p-4 pb-3 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Category badge */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full mb-2">
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </span>
            
            {/* Title */}
            <h3 className="font-semibold text-gray-900 truncate text-sm leading-tight" title={listing.title}>
              {listing.title}
            </h3>
          </div>
          
          {/* Price badge */}
          <div className="flex-shrink-0">
            {listing.isFree ? (
              <span className="inline-flex items-center px-2 py-1 text-xs font-bold bg-green-100 text-green-700 rounded">
                FREE
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 text-sm font-bold text-gray-900">
                ${listing.price}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Body */}
      <div className="p-4 pt-3">
        {/* Description */}
        <p className="text-gray-600 text-xs line-clamp-2 mb-3 leading-relaxed">
          {listing.description}
        </p>
        
        {/* Stats row */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
          {/* Rating */}
          <div className="flex items-center gap-1">
            <span className="text-yellow-500">★</span>
            <span>
              {listing.reviewCount > 0 && typeof listing.rating === 'number' ? listing.rating.toFixed(1) : '—'}
            </span>
            {listing.reviewCount > 0 && (
              <span className="text-gray-400">({listing.reviewCount})</span>
            )}
          </div>
          
          {/* Sales */}
          <div className="flex items-center gap-1">
            <span>📥</span>
            <span>{listing.totalSales} sales</span>
          </div>
          
          {/* Node count */}
          <div className="flex items-center gap-1">
            <span>📋</span>
            <span>{listing.nodeCount} nodes</span>
          </div>
        </div>
        
        {/* Seller info */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
          <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
            {listing.sellerDisplayName?.charAt(0)?.toUpperCase() || '?'}
          </span>
          <span className="truncate" title={listing.sellerDisplayName}>Created by {listing.sellerDisplayName}</span>
        </div>
        
        {/* Features badges */}
        <div className="flex flex-wrap gap-1 mb-3">
          {listing.hasCoachingTips && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
              💡 Coaching Tips
            </span>
          )}
          {listing.featured && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
              ⭐ Featured
            </span>
          )}
        </div>
        
        {/* Action buttons */}
        {showActions && (
          <div>
            {acquireError && (
              <p className="text-xs text-red-600 mb-2">{acquireError}</p>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewDetails}
                className="flex-1"
              >
                Preview
              </Button>
              
              {isOwnListing ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/marketplace/sell?edit=${listing.id}`)}
                  className="flex-1"
                >
                  Edit
                </Button>
              ) : (isPurchased || acquired) ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled
                  className="flex-1"
                >
                  ✓ Owned
                </Button>
              ) : listing.isFree ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleQuickAcquire}
                  disabled={acquiring}
                  className="flex-1"
                >
                  {acquiring ? 'Adding...' : 'Get Script'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleViewDetails}
                  className="flex-1"
                >
                  {`$${listing.price}`}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MarketplaceListingCard;
